import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Loader2,
  UploadCloud,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminFetch, formatBytes } from '@/lib/api';

type DatasetStats = {
  totals: { assets: number; photos: number; videos: number; storage_bytes: number };
  export_status: Record<string, number>;
  review_status: Record<string, number>;
  approved_for_export: number;
  by_month: { month: string; count: number }[];
  embeddings: { active_vectors: number; animals_enrolled: number };
  active_model_version: string | null;
  roboflow: { workspace: string | null; project: string | null; classes: string[]; configured: boolean };
  readiness: { level: string; message: string }[];
};

type RoboflowStatus = {
  configured: boolean;
  missing: string[];
  workspace: string | null;
  project: string | null;
  app_url: string | null;
  annotate_url: string | null;
  versions_url: string | null;
  train_url: string | null;
  project_data: {
    id: string | null;
    name: string | null;
    type: string | null;
    images: number;
    unannotated: number;
    versions_count: number;
    splits: Record<string, number>;
    classes: Record<string, number>;
  } | null;
  versions: {
    id: string | null;
    name: string | null;
    created: string | null;
    images: number;
    has_model: boolean;
    model_type: string | null;
    map: number | null;
  }[];
  error: string | null;
};

type StageState = 'done' | 'active' | 'todo' | 'blocked';

type Stage = {
  id: string;
  step: number;
  title: string;
  summary: string;
  state: StageState;
  metric?: string;
};

export function DashboardPage() {
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const datasetQuery = useQuery({
    queryKey: ['admin-dataset-stats'],
    queryFn: () => adminFetch<{ dataset: DatasetStats }>('/ml/dataset/stats'),
  });

  const roboflowQuery = useQuery({
    queryKey: ['admin-roboflow-status'],
    queryFn: () => adminFetch<{ roboflow: RoboflowStatus }>('/ml/roboflow/status'),
    refetchInterval: 60_000,
  });

  const exportZip = useMutation({
    mutationFn: () =>
      adminFetch<{ export: { download_url: string | null } }>('/ml/exports', {
        method: 'POST',
        body: JSON.stringify({ format: 'zip_bundle', only_approved: true }),
      }),
    onSuccess: (result) => {
      if (result.export.download_url) window.open(result.export.download_url, '_blank');
      datasetQuery.refetch();
    },
    onError: (err) =>
      setFeedback({ kind: 'error', message: err instanceof Error ? err.message : 'Erro ao gerar ZIP.' }),
  });

  const uploadRoboflow = useMutation({
    mutationFn: () => {
      setFeedback(null);
      return adminFetch<{ export: { asset_count: number | null; error_message: string | null } }>('/ml/exports', {
        method: 'POST',
        body: JSON.stringify({ format: 'roboflow_upload', only_approved: true }),
      });
    },
    onSuccess: (result) => {
      const count = result.export.asset_count ?? 0;
      setFeedback({
        kind: result.export.error_message ? 'error' : 'success',
        message:
          `${count} imagem(ns) enviada(s) ao Roboflow.` +
          (result.export.error_message ? ` ${result.export.error_message}` : ''),
      });
      datasetQuery.refetch();
      roboflowQuery.refetch();
    },
    onError: (err) => {
      setFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Erro no envio ao Roboflow.',
      });
    },
  });

  if (datasetQuery.isLoading) {
    return <p className="text-neutral-400">Carregando pipeline…</p>;
  }
  if (datasetQuery.error) {
    return (
      <p className="text-red-400">
        {datasetQuery.error instanceof Error ? datasetQuery.error.message : 'Erro'}
      </p>
    );
  }

  const ds = datasetQuery.data?.dataset;
  if (!ds) return null;

  const rf = roboflowQuery.data?.roboflow;
  const pending = ds.review_status?.pending ?? 0;
  const approved = ds.approved_for_export ?? 0;
  const exported = ds.export_status?.exported ?? 0;
  const unannotated = rf?.project_data?.unannotated ?? 0;
  const rfImages = rf?.project_data?.images ?? 0;
  const versionsWithModel = (rf?.versions ?? []).filter((v) => v.has_model).length;

  const stages: Stage[] = [
    {
      id: 'collect',
      step: 1,
      title: 'Coletar',
      summary: 'Fotos e vídeos capturados no app/portal Manejai vão para o cofre ML.',
      state: ds.totals.assets > 0 ? 'done' : 'active',
      metric: `${ds.totals.assets} no cofre · ${formatBytes(ds.totals.storage_bytes)}`,
    },
    {
      id: 'curate',
      step: 2,
      title: 'Curar',
      summary: 'Aprove só o que serve para treino (focinho/cabeça nítidos).',
      state:
        pending > 0 ? 'active' : approved > 0 || exported > 0 ? 'done' : ds.totals.assets > 0 ? 'active' : 'todo',
      metric: `${pending} pendente(s) · ${approved} aprovado(s)`,
    },
    {
      id: 'upload',
      step: 3,
      title: 'Enviar ao Roboflow',
      summary: 'Sobe as imagens aprovadas para o dataset na nuvem.',
      state: !ds.roboflow.configured
        ? 'blocked'
        : approved > 0
          ? 'active'
          : exported > 0 || rfImages > 0
            ? 'done'
            : 'todo',
      metric: approved > 0 ? `${approved} prontas para envio` : `${exported} já exportadas no Manejai`,
    },
    {
      id: 'annotate',
      step: 4,
      title: 'Anotar',
      summary: 'Desenhe caixas: cattle-nose, cattle-face, cattle-head.',
      state: !rf?.configured
        ? 'blocked'
        : unannotated > 0
          ? 'active'
          : rfImages > 0
            ? 'done'
            : 'todo',
      metric: rf?.configured
        ? `${rfImages} no Roboflow · ${unannotated} sem anotação`
        : 'Configure a API key',
    },
    {
      id: 'train',
      step: 5,
      title: 'Gerar versão & treinar',
      summary: 'No Roboflow: gere uma versão do dataset e treine o detector.',
      state: !rf?.configured
        ? 'blocked'
        : versionsWithModel > 0
          ? 'done'
          : rfImages > 0 && unannotated === 0
            ? 'active'
            : 'todo',
      metric: rf?.project_data
        ? `${rf.project_data.versions_count} versão(ões) · ${versionsWithModel} com modelo`
        : '—',
    },
    {
      id: 'publish',
      step: 6,
      title: 'Publicar no app',
      summary: 'Registre o modelo treinado no Manejai para o app baixar o bundle.',
      state: ds.active_model_version ? 'done' : versionsWithModel > 0 ? 'active' : 'todo',
      metric: ds.active_model_version
        ? `Bundle ativo: ${ds.active_model_version}`
        : `${ds.embeddings.animals_enrolled} animais com embedding`,
    },
  ];

  const activeStep = stages.find((s) => s.state === 'active') ?? stages.find((s) => s.state === 'todo');

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Pipeline ML</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-400">
            Fluxo simples: coletar → curar → Roboflow → anotar → treinar → publicar no app.
            {activeStep ? (
              <>
                {' '}
                Agora foque na etapa <span className="text-white">{activeStep.step}. {activeStep.title}</span>.
              </>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            datasetQuery.refetch();
            roboflowQuery.refetch();
          }}
          className="rounded-lg border border-neutral-700 px-3 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
        >
          Atualizar
        </button>
      </div>

      {feedback && (
        <p className={feedback.kind === 'error' ? 'text-sm text-red-400' : 'text-sm text-emerald-400'}>
          {feedback.message}
        </p>
      )}

      {!ds.roboflow.configured && (
        <div className="rounded-xl border border-amber-800 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          Configure no servidor: <code className="text-amber-50">ROBOFLOW_API_KEY</code>,{' '}
          <code className="text-amber-50">ROBOFLOW_WORKSPACE</code> e{' '}
          <code className="text-amber-50">ROBOFLOW_PROJECT</code>. Sem isso, as etapas 3–5 ficam bloqueadas.
        </div>
      )}

      <ol className="space-y-3">
        {stages.map((stage) => (
          <li
            key={stage.id}
            className={`rounded-xl border px-4 py-4 ${
              stage.state === 'active'
                ? 'border-violet-500/60 bg-violet-950/30'
                : stage.state === 'blocked'
                  ? 'border-neutral-800 bg-neutral-900/30 opacity-70'
                  : 'border-neutral-800 bg-neutral-900/40'
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 gap-3">
                <StageIcon state={stage.state} step={stage.step} />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-medium text-white">
                      {stage.step}. {stage.title}
                    </h2>
                    <StateBadge state={stage.state} />
                  </div>
                  <p className="mt-1 text-sm text-neutral-400">{stage.summary}</p>
                  {stage.metric && <p className="mt-2 text-xs text-neutral-500">{stage.metric}</p>}
                </div>
              </div>

              <StageActions
                stageId={stage.id}
                ds={ds}
                rf={rf}
                approved={approved}
                exportZip={exportZip}
                uploadRoboflow={uploadRoboflow}
                refreshing={roboflowQuery.isFetching}
              />
            </div>

            {stage.id === 'train' && rf?.versions && rf.versions.length > 0 && (
              <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-800">
                <table className="w-full text-left text-xs">
                  <thead className="bg-neutral-900 text-neutral-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Versão</th>
                      <th className="px-3 py-2 font-medium">Imagens</th>
                      <th className="px-3 py-2 font-medium">Modelo</th>
                      <th className="px-3 py-2 font-medium">mAP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {rf.versions.map((v) => (
                      <tr key={v.id ?? v.name}>
                        <td className="px-3 py-2 text-neutral-200">{v.name ?? '—'}</td>
                        <td className="px-3 py-2 text-neutral-400">{v.images}</td>
                        <td className="px-3 py-2 text-neutral-400">
                          {v.has_model ? v.model_type ?? 'sim' : 'ainda não treinado'}
                        </td>
                        <td className="px-3 py-2 text-neutral-400">
                          {v.map != null ? Number(v.map).toFixed(2) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {stage.id === 'annotate' && rf?.project_data?.classes && Object.keys(rf.project_data.classes).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(rf.project_data.classes).map(([name, count]) => (
                  <span
                    key={name}
                    className="rounded-full border border-neutral-700 bg-neutral-950 px-2.5 py-1 text-xs text-neutral-300"
                  >
                    {name}: {count}
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>

      {rf?.error && (
        <p className="text-sm text-red-400">Não foi possível ler o Roboflow: {rf.error}</p>
      )}

      <section className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-4">
        <h2 className="text-sm font-semibold text-neutral-300">Atalhos</h2>
        <div className="mt-3 flex flex-wrap gap-2 text-sm">
          <Link to="/media" className="rounded-lg border border-neutral-700 px-3 py-2 hover:bg-neutral-900">
            Abrir curadoria
          </Link>
          <Link to="/clients" className="rounded-lg border border-neutral-700 px-3 py-2 hover:bg-neutral-900">
            Ver clientes
          </Link>
          {rf?.app_url && (
            <a
              href={rf.app_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-white hover:bg-violet-500"
            >
              Abrir projeto no Roboflow <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </section>
    </div>
  );
}

function StageIcon({ state, step }: { state: StageState; step: number }) {
  if (state === 'done') {
    return <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />;
  }
  if (state === 'active') {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-600 text-[10px] font-bold text-white">
        {step}
      </span>
    );
  }
  return <Circle className="mt-0.5 h-5 w-5 shrink-0 text-neutral-600" />;
}

function StateBadge({ state }: { state: StageState }) {
  const map: Record<StageState, string> = {
    done: 'bg-emerald-950 text-emerald-300 border-emerald-800',
    active: 'bg-violet-950 text-violet-200 border-violet-700',
    todo: 'bg-neutral-900 text-neutral-400 border-neutral-700',
    blocked: 'bg-neutral-900 text-neutral-500 border-neutral-800',
  };
  const label: Record<StageState, string> = {
    done: 'concluída',
    active: 'agora',
    todo: 'próxima',
    blocked: 'bloqueada',
  };
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${map[state]}`}>
      {label[state]}
    </span>
  );
}

function StageActions({
  stageId,
  ds,
  rf,
  approved,
  exportZip,
  uploadRoboflow,
  refreshing,
}: {
  stageId: string;
  ds: DatasetStats;
  rf?: RoboflowStatus;
  approved: number;
  exportZip: { mutate: () => void; isPending: boolean };
  uploadRoboflow: { mutate: () => void; isPending: boolean };
  refreshing: boolean;
}) {
  if (stageId === 'collect') {
    return (
      <p className="max-w-xs text-right text-xs text-neutral-500">
        Só entram capturas feitas no app ou portal Manejai (com sessão de câmera).
      </p>
    );
  }

  if (stageId === 'curate') {
    return (
      <Link
        to="/media?review_status=pending"
        className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-black"
      >
        Ir para curadoria
      </Link>
    );
  }

  if (stageId === 'upload') {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => uploadRoboflow.mutate()}
          disabled={uploadRoboflow.isPending || !ds.roboflow.configured || approved === 0}
          className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {uploadRoboflow.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UploadCloud className="h-4 w-4" />
          )}
          Enviar aprovadas
        </button>
        <button
          type="button"
          onClick={() => exportZip.mutate()}
          disabled={exportZip.isPending || approved === 0}
          className="rounded-lg border border-neutral-700 px-3 py-2 text-sm hover:bg-neutral-900 disabled:opacity-50"
        >
          Baixar ZIP
        </button>
      </div>
    );
  }

  if (stageId === 'annotate') {
    return rf?.annotate_url ? (
      <a
        href={rf.annotate_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white"
      >
        Anotar no Roboflow <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : (
      <span className="text-xs text-neutral-500">{refreshing ? 'Consultando…' : 'Sem link'}</span>
    );
  }

  if (stageId === 'train') {
    return (
      <div className="flex flex-wrap gap-2">
        {rf?.train_url && (
          <a
            href={rf.train_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white"
          >
            Gerar versão / treinar <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {rf?.versions_url && (
          <a
            href={rf.versions_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-3 py-2 text-sm hover:bg-neutral-900"
          >
            Ver versões <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    );
  }

  if (stageId === 'publish') {
    return (
      <p className="max-w-xs text-right text-xs text-neutral-500">
        Depois do treino, baixe o modelo (TFLite) no Roboflow e registre via API{' '}
        <code className="text-neutral-400">/ml/models</code> (próxima tela).
      </p>
    );
  }

  return null;
}
